import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const ContactSubmissionManagement = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    priority: ''
  });

  useEffect(() => {
    loadSubmissions();
  }, [filters]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      let query = supabase
        ?.from('contact_submissions')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      const { data, error } = await query;

      if (error) throw error;

      setSubmissions(data);
    } catch (error) {
      console.error('Load submissions error:', error);
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      
      if (newStatus === 'resolved') {
        updateData.resolved_at = new Date()?.toISOString();
      }

      const { error } = await supabase
        ?.from('contact_submissions')
        ?.update(updateData)
        ?.eq('id', submissionId);

      if (error) throw error;

      toast.success('Status updated successfully');
      loadSubmissions();
    } catch (error) {
      console.error('Update status error:', error);
      toast.error('Failed to update status');
    }
  };

  const updateSubmissionPriority = async (submissionId, newPriority) => {
    try {
      const { error } = await supabase
        ?.from('contact_submissions')
        ?.update({ priority: newPriority })
        ?.eq('id', submissionId);

      if (error) throw error;

      toast.success('Priority updated successfully');
      loadSubmissions();
    } catch (error) {
      console.error('Update priority error:', error);
      toast.error('Failed to update priority');
    }
  };

  const saveAdminNotes = async (submissionId) => {
    try {
      const { error } = await supabase
        ?.from('contact_submissions')
        ?.update({ admin_notes: adminNotes })
        ?.eq('id', submissionId);

      if (error) throw error;

      toast.success('Notes saved successfully');
      setSelectedSubmission(null);
      setAdminNotes('');
      loadSubmissions();
    } catch (error) {
      console.error('Save notes error:', error);
      toast.error('Failed to save notes');
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const { error } = await supabase
        ?.from('contact_submissions')
        ?.delete()
        ?.eq('id', submissionId);

      if (error) throw error;

      toast.success('Submission deleted successfully');
      loadSubmissions();
    } catch (error) {
      console.error('Delete submission error:', error);
      toast.error('Failed to delete submission');
    }
  };

  const openSubmissionDetails = (submission) => {
    setSelectedSubmission(submission);
    setAdminNotes(submission?.admin_notes || '');
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-primary/10 text-primary border-primary/20',
      in_progress: 'bg-warning/10 text-warning border-warning/20',
      resolved: 'bg-success/10 text-success border-success/20',
      spam: 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-error/10 text-error border-error/20',
      high: 'bg-warning/10 text-warning border-warning/20',
      medium: 'bg-primary/10 text-primary border-primary/20',
      low: 'bg-muted text-muted-foreground border-border'
    };
    return colors?.[priority] || 'bg-muted text-muted-foreground border-border';
  };

  const getStatusIcon = (status) => {
    const icons = {
      new: 'Mail',
      in_progress: 'Clock',
      resolved: 'CheckCircle',
      spam: 'AlertCircle'
    };
    return icons?.[status] || 'MessageSquare';
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['new', 'in_progress', 'resolved', 'spam']?.map((status) => {
          const count = submissions?.filter(s => s?.status === status)?.length || 0;
          return (
            <div key={status} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground capitalize">{status?.replace('_', ' ')}</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{count}</p>
                </div>
                <div className={`p-3 rounded-lg ${getStatusColor(status)?.split(' ')?.[0]}`}>
                  <Icon name={getStatusIcon(status)} size={24} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <select
              value={filters?.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="spam">Spam</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
            <select
              value={filters?.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e?.target?.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={loadSubmissions}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
            >
              <Icon name="RefreshCw" size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2" />
                    Loading submissions...
                  </td>
                </tr>
              ) : submissions?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    <Icon name="Inbox" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No contact submissions found</p>
                  </td>
                </tr>
              ) : (
                submissions?.map((submission) => (
                  <tr key={submission?.id} className="hover:bg-muted/30 transition-smooth">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{submission?.name}</p>
                        <p className="text-xs text-muted-foreground">{submission?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground max-w-xs truncate">{submission?.subject}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={submission?.priority}
                        onChange={(e) => updateSubmissionPriority(submission?.id, e?.target?.value)}
                        className={`px-2 py-1 border rounded text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary capitalize ${getPriorityColor(submission?.priority)}`}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={submission?.status}
                        onChange={(e) => updateSubmissionStatus(submission?.id, e?.target?.value)}
                        className={`px-2 py-1 border rounded text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary capitalize ${getStatusColor(submission?.status)}`}
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="spam">Spam</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(submission?.created_at)?.toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openSubmissionDetails(submission)}
                          className="p-1.5 text-primary hover:bg-primary/10 rounded transition-smooth"
                          title="View Details"
                        >
                          <Icon name="Eye" size={16} />
                        </button>
                        <button
                          onClick={() => deleteSubmission(submission?.id)}
                          className="p-1.5 text-error hover:bg-error/10 rounded transition-smooth"
                          title="Delete"
                        >
                          <Icon name="Trash" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Submission Details Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-foreground">Submission Details</h3>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Contact Information</h4>
                <div className="space-y-2 bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{selectedSubmission?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <a href={`mailto:${selectedSubmission?.email}`} className="text-sm text-primary hover:underline">
                      {selectedSubmission?.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {new Date(selectedSubmission?.created_at)?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Subject</h4>
                <p className="text-sm text-foreground bg-muted/30 rounded-lg p-3">
                  {selectedSubmission?.subject}
                </p>
              </div>

              {/* Message */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Message</h4>
                <p className="text-sm text-foreground bg-muted/30 rounded-lg p-4 whitespace-pre-wrap">
                  {selectedSubmission?.message}
                </p>
              </div>

              {/* Admin Notes */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Admin Notes</h4>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e?.target?.value)}
                  rows={4}
                  placeholder="Add internal notes about this submission..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <button
                  onClick={() => saveAdminNotes(selectedSubmission?.id)}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
                >
                  <Icon name="Save" size={16} />
                  Save Notes
                </button>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="flex-1 bg-muted text-foreground px-4 py-2 rounded-lg hover:bg-muted/80 transition-smooth"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissionManagement;
